import { Box, Overlay } from "@modernatx-internal/component-library";
import React from "react";

import { ContentAction, ContentActionProps } from "../types";

declare global {
  interface Window {
    MODERNA_COOKIE_CONSENT?: {
      open: () => void;
    };
  }
}

interface ActionContextValue {
  overlayContent: React.ReactNode;
  overlayContentSet: (content: React.ReactNode) => void;
  overlayVisible: boolean;
  overlayVisibleSet: (visible: boolean) => void;
}

const ActionContext = React.createContext<ActionContextValue>({
  overlayContent: null,
  overlayContentSet: () => {},
  overlayVisible: false,
  overlayVisibleSet: () => {}
});

export const ActionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const clickingContent = React.useRef(false);
  const [overlayContent, overlayContentSet] =
    React.useState<ActionContextValue["overlayContent"]>(null);
  const [overlayVisible, overlayVisibleSet] = React.useState(false);

  const handleOutsideClick = React.useCallback(() => {
    if (!clickingContent.current) {
      overlayVisibleSet(false);
    }
    clickingContent.current = false;
  }, []);

  return (
    <ActionContext.Provider
      value={{ overlayContent, overlayContentSet, overlayVisible, overlayVisibleSet }}
    >
      {children}
      <Overlay visible={overlayVisible} onDismiss={() => overlayVisibleSet(false)}>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "center",
            width: "100%"
          }}
          onClick={handleOutsideClick}
        >
          <Box
            sx={{
              backgroundColor: "surface-neutral",
              borderRadius: "radius-3",
              boxShadow: "shadow-300",
              px: 4,
              py: 4
            }}
            onClick={() => {
              clickingContent.current = true;
            }}
          >
            {overlayContent}
          </Box>
        </Box>
      </Overlay>
    </ActionContext.Provider>
  );
};

export const useAction = (defaultAction?: ContentActionProps<ContentAction>) => {
  const overlayHideTimeout = React.useRef<ReturnType<typeof setTimeout>>();
  const { overlayContentSet, overlayVisible, overlayVisibleSet } = React.useContext(ActionContext);

  const handleActionClick = React.useCallback(
    async (e: React.MouseEvent, actionOverride?: ContentActionProps<ContentAction>) => {
      const resolvedAction = actionOverride ?? defaultAction;

      if (resolvedAction?.type === "CopyText") {
        e.preventDefault();

        const { elementSelector, text, successMessage } = resolvedAction.props;
        let textToCopy = text;

        if (elementSelector) {
          const element = document.querySelector(elementSelector);
          textToCopy = element?.textContent || undefined;
        }

        if (textToCopy) {
          await navigator.clipboard.writeText(textToCopy);

          overlayContentSet(successMessage);
          overlayVisibleSet(true);

          overlayHideTimeout.current = setTimeout(() => {
            overlayVisibleSet(false);
          }, 2000);
        }
      } else if (resolvedAction?.type === "OpenCookieConsent") {
        e.preventDefault();
        window.MODERNA_COOKIE_CONSENT?.open?.();
      }
    },
    [defaultAction, overlayContentSet, overlayVisibleSet]
  );

  if (!overlayVisible && overlayHideTimeout.current !== undefined) {
    clearTimeout(overlayHideTimeout.current);
    overlayHideTimeout.current = undefined;
  }

  return { handleActionClick };
};
